import React, { useEffect, useState } from "react";
import { createStyles } from "@material-ui/core/styles";
import withStyles from "@material-ui/core/styles/withStyles";
import {
  WithStyles,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from "@material-ui/core";
import * as types from "../../../redux/types";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { niceDate } from "../../FormatHelpers";
import InvoicingProfileLinkContainer from "../../../containers/links/InvoicingProfileLinkContainer";
import { useTranslate } from "../../../services/appLanguageService";
import ServiceCategoriesContainer from "../../../containers/contracts/businessAreas/ServiceCategoriesContainer";
import TaskOptionsContainer from "../../../containers/contracts/businessAreas/TaskOptionsContainer";
import ResourceLink from "../../links/ResourceLink";
import store from "../../../redux/store";
import { setTitle } from "../../../redux/reducers/tabRouter/tabTitle/actions";
import { TabComponentProps } from "../../../redux/types";
import { mapTabRouteProps } from "../../../helpers/routesHelper";

const styles = (theme: any) =>
  createStyles({
    root: {
      flexGrow: 1
    },
    paper: {
      marginTop: 20
    },
    editButton: {
      marginLeft: 20
    },
    expandHeader: {
      color: theme.palette.text.secondary
    },
    bold: {
      fontWeight: "bold"
    },
    tableContainer: {
      padding: 0
    }
  });

interface BusinessAreaProps
  extends WithStyles<typeof styles>,
    TabComponentProps {
  businessarea: types.BusinessArea;
  contractingProfile: types.ContractingProfile | null;
}

const BusinessArea: React.FC<BusinessAreaProps> = (props) => {
  const { classes, businessarea, contractingProfile, tabId } = props;
  const t = useTranslate("BusinessAreaPage");

  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    store.dispatch(setTitle(businessarea.name, tabId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessarea]);

  return (
    <div className={classes.root}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={5}>
          <TableContainer component={Paper} className={classes.paper}>
            <Table size="small" aria-label="a dense table">
              <TableBody>
                <TableRow key={0}>
                  <TableCell align="left" className={classes.bold}>
                    {t("nameLabel")}
                  </TableCell>
                  <TableCell align="right">{businessarea.name}</TableCell>
                </TableRow>
                <TableRow key={1}>
                  <TableCell align="left" className={classes.bold}>
                    {t("invoicingProfileLabel")}
                  </TableCell>
                  <TableCell align="right">
                    {businessarea.invoicingprofile ? (
                      <InvoicingProfileLinkContainer
                        invoicingProfileId={businessarea.invoicingprofile}
                        {...mapTabRouteProps(props)}
                      />
                    ) : (
                      ""
                    )}
                  </TableCell>
                </TableRow>
                <TableRow key={2}>
                  <TableCell align="left" className={classes.bold}>
                    {t("contractingProfileLabel")}
                  </TableCell>
                  <TableCell align="right">
                    {contractingProfile && (
                      <ResourceLink
                        label={contractingProfile.name}
                        url="contracts/contractingprofiles"
                        id={contractingProfile.id}
                      />
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
              className={classes.expandHeader}
              onClick={() =>
                expanded ? setExpanded(false) : setExpanded(true)
              }
            >
              <Typography>
                {expanded ? t("hideLabel") : t("showMoreLabel")}
              </Typography>
            </AccordionSummary>
            <TableContainer
              component={AccordionDetails}
              className={classes.tableContainer}
            >
              <Table size="small" aria-label="a dense table">
                <TableBody>
                  <TableRow key={0}>
                    <TableCell align="left" className={classes.bold}>
                      {t("notesLabel")}
                    </TableCell>
                    <TableCell align="right">{businessarea.notes}</TableCell>
                  </TableRow>
                  <TableRow key={1}>
                    <TableCell align="left" className={classes.bold}>
                      {t("createdAtLabel")}
                    </TableCell>
                    <TableCell align="right">
                      {niceDate(businessarea.created_at)}
                    </TableCell>
                  </TableRow>
                  <TableRow key={2}>
                    <TableCell align="left" className={classes.bold}>
                      {t("updatedAtLabel")}
                    </TableCell>
                    <TableCell align="right">
                      {niceDate(businessarea.updated_at)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Accordion>
          <ServiceCategoriesContainer
            businessAreaId={businessarea.id}
            editable={true}
            {...mapTabRouteProps(props)}
          />
        </Grid>
        <Grid item xs={12} sm={7}>
          <TaskOptionsContainer
            showNewButton={false}
            businessAreaId={businessarea.id}
            {...mapTabRouteProps(props)}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default withStyles(styles)(BusinessArea);
